<!-- This example requires Tailwind CSS v2.0+ -->
<template>
  <TransitionRoot
    as="template"
    :show="open"
  >
    <Dialog
      as="div"
      class="fixed inset-0 z-10 overflow-y-auto"
      @close="$emit('close')"
    >
      <div
        class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay
            class="fixed inset-0 transition-opacity bg-gray-500/75"
          />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span
          class="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >&#8203;</span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="relative inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
          >
            <div class="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
              <button
                type="button"
                class="text-gray-400 bg-white rounded-md hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                @click="$emit('close')"
              >
                <span class="sr-only">Close</span>
                <XIcon
                  class="w-6 h-6"
                  aria-hidden="true"
                />
              </button>
            </div>
            <div class="sm:flex sm:items-start">
              <div
                class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-teal-100 rounded-full sm:mx-0 sm:h-10 sm:w-10"
              >
                <ld-icon
                  icon="PhoneOutgoingIcon"
                  class="w-4 h-4"
                />
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <DialogTitle
                  as="h3"
                  class="text-lg font-medium leading-6 text-gray-900"
                >
                  Conference in
                </DialogTitle>
                <div class="mt-2">
                  <LDInput
                    v-model="number"
                    label="Number To Conference In"
                    name="number"
                  />
                </div>
              </div>
            </div>
            <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                v-if="!disable"
                type="button"
                class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-teal-600 border border-transparent rounded-md shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:ml-3 sm:w-auto sm:text-sm"
                @click="dial"
              >
                Conference In
              </button>
              <button
                type="button"
                class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:mt-0 sm:w-auto sm:text-sm"
                @click="$emit('close')"
              >
                Cancel
              </button>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { defineComponent, ref } from 'vue'
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { XIcon } from '@heroicons/vue/outline'
import LDInput from '/@/components/ui/form/LDInput.vue'
import useLetsDialPhone from '/@/services/letsDialPhone/useLetsDialPhone'
import useLetsDialAPI from '/@/services/letsDialAPI/useLetsDialAPI'
import { useAuthStore } from '/@/store/authStore'
import { CallType } from '@letsdial/api/src/lib/types/Call'
import { useCallStore } from '/@/store/callStore'

const props = withDefaults(defineProps<{ open: boolean }>(), {
  open: () => false,
})
const emit = defineEmits<{ close: [] }>()
const number = ref('')
const auth = useAuthStore()
const callStore = useCallStore()
const { phone } = useLetsDialPhone()
const api = useLetsDialAPI()
const disable = ref(false)
// const open = ref(true)
const dial = async () => {
  if (disable.value) {
    return
  }
  disable.value = true
  if (auth.user?.id && auth.selectedOrganization?.id) {
    const call = await api.calls.post({
      type: 'calls',
      attributes: {
        number: number.value,
        callType: CallType.conferenceCalls,
        additionalData: { roomCode: callStore.call?.id ?? auth.user.id },
      },
      relationships: {
        user: { data: { type: 'users', id: auth.user.id } },
        parent: {
          data: callStore.call?.id
            ? { type: 'calls', id: callStore.call.id }
            : null,
        },
        organization: {
          data: {
            type: 'organizations',
            id: auth.selectedOrganization?.id,
          },
        },
      },
    })
    if (call.data) {
      if (!callStore.conferenceRoomId && callStore.call.id) {
        await phone.referToConference(callStore.call?.id ?? auth.user.id)
        const conferenceCall = await api.calls.get(callStore.call.id)
        phone.joinConference(
          callStore.call?.id ?? auth.user.id,
          conferenceCall.data?.attributes.asteriskServerIp,
        )
      }
    }
  }
  disable.value = false
  emit('close')
}
</script>
